import { useCallback, useEffect, useId, useState } from 'react';
import { PencilIcon, PlusIcon, TrashIcon } from '@/components/catalog/icons';
import { CatalogStatusBadge } from '@/components/catalog-admin/CatalogMasterBits';
import { TargetPeriodFormModal } from '@/components/targets/TargetPeriodFormModal';
import { Notice } from '@/components/ui/Notice';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { formatDateOnly, orDash, pluralise } from '@/lib/format';
import { denialReason, type Ability } from '@/lib/permissions';
import { TARGET_PERIOD_IN_USE, targetPeriodKindLabel } from '@/lib/targets';
import type { Cycle, Paginated, TargetPeriodRecord } from '@/lib/types';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Modal,
  Panel,
  PanelBody,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';

/* -------------------------------------------------------------------------
 * Target periods, managed from /catalog.
 *
 * ## Why here and not from the targets screen
 *
 * `UserTargetsModal` is PER PERSON and a period is GLOBAL — every target in the
 * business reads through the same handful of windows, and moving a quarter's
 * boundary rewrites what somebody else's number means. Offering the create from
 * inside one person's dialog would put a business-wide edit behind whichever
 * name happened to be open, and would offer it to the many people who can read
 * targets and cannot write a period.
 *
 * `/catalog` is where this app already keeps exactly this shape of thing:
 * readable by every role, every write admin-only, and a screen that documents
 * what the CRM is CONFIGURED with rather than what a picker offers. The guard
 * profile matches to the letter — `target-periods.routes.ts` is
 * `authenticate` + `requireRole(...ADMIN_ROLES)` on every write, the same
 * profile as the reference masters.
 *
 * It also sits with Cycles and Intakes, which is where it belongs: a period is a
 * named window of time, and one of its four kinds literally names a cycle.
 *
 * ## Loading
 *
 * `perPage: 200` is this endpoint's ceiling (default 50), and `isActive` is
 * deliberately ABSENT — unfiltered returns retired rows too, which is the whole
 * point of a catalogue screen and exactly what a picker must not do. `sort` and
 * `order` are left at their server defaults, `startDate` ascending: a list of
 * periods is a timeline and a timeline reads forwards, so re-sorting here would
 * make this screen disagree with the picker it documents.
 * --------------------------------------------------------------------- */

/** This endpoint's own ceiling: 1..200, default 50. Not the entities' 100. */
const PERIOD_QUERY = { page: 1, perPage: 200 } as const;

const SKELETON_ROWS = 5;

const stroke = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

/**
 * A calendar with a marked span — a window of time, which is what a period is.
 *
 * Deliberately not the plain calendar `CycleIcon` uses: a cycle and a target
 * period are both windows, and two identical marks would invite the conflation
 * the two lists exist to keep apart.
 */
function PeriodRangeIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M8 3v4" />
      <path d="M16 3v4" />
      <path d="M3 11h18" />
      <path d="M7.5 15.5h9" />
    </svg>
  );
}

export interface TargetPeriodsCardProps {
  ability: Ability;
  /** The loaded cycle list, for the `kind: 'cycle'` picker in the form. */
  cycles: Cycle[];
  cyclesLoading: boolean;
  /** The cycle read FAILED, as against having returned nothing. */
  cyclesFailed: boolean;
  /**
   * ⚠️ ACCEPTED AND NO LONGER READ. The "first page only — a cycle may be
   * missing" hint it fed was removed in the 2026-09-05 sweep. Kept on the
   * interface so `CatalogPage` still compiles; delete it there and here
   * together if it is never used again.
   */
  cyclesTruncated: boolean;
}

interface ListState {
  result: Paginated<TargetPeriodRecord> | null;
  error: string | null;
  loading: boolean;
}

export function TargetPeriodsCard({
  ability,
  cycles,
  cyclesLoading,
  cyclesFailed,
}: TargetPeriodsCardProps) {
  const headingId = useId();
  const [state, setState] = useState<ListState>({ result: null, error: null, loading: true });
  const [nonce, setNonce] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    /*
     * Deferred a tick rather than fired in the effect body. The fetch flips the
     * loading flag synchronously, and doing that inside the effect cascades a
     * render — the same reason `useMasterList` on this page and `CyclesPage`
     * both defer. Cleared on unmount so a fetch is never started for a card that
     * has gone away.
     */
    const timer = setTimeout(() => {
      setState((previous) => ({ ...previous, loading: true }));
      void api
        .listTargetPeriods(PERIOD_QUERY, signal)
        .then((result) => {
          if (signal.aborted) return;
          setState({ result, error: null, loading: false });
        })
        .catch((caught: unknown) => {
          if (signal.aborted) return;
          setState((previous) => ({
            ...previous,
            error: caught instanceof Error ? caught.message : 'Failed to load target periods',
          }));
        })
        .finally(() => {
          if (!signal.aborted) setState((previous) => ({ ...previous, loading: false }));
        });
    }, 0);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  const rows = state.result?.data ?? [];
  const total = state.result?.meta.total ?? null;
  const retired = rows.filter((row) => !row.isActive).length;
  const canWrite =
    ability.canCreate('reference-planning') || ability.canUpdate('reference-planning');

  return (
    <>
      <Panel className="min-w-0" aria-labelledby={headingId}>
        <PanelBody className="flex h-full flex-col gap-3">
          <div className="flex items-start gap-2.5">
            <span aria-hidden="true" className="shrink-0 text-fg-subtle">
              <PeriodRangeIcon />
            </span>
            <div className="min-w-0">
              <h3 id={headingId} className="m-0 text-sm font-semibold text-fg">
                Target periods
              </h3>
            </div>
          </div>

          <div className="min-h-5">
            {state.loading && state.result === null ? (
              <Skeleton className="h-4 w-24" />
            ) : state.error ? (
              <span className="text-xs text-danger">Couldn’t load this list.</span>
            ) : (
              <div className="flex flex-wrap items-center gap-1.5">
                <Badge
                  tone={total === 0 ? 'warning' : 'neutral'}
                  variant={total === 0 ? 'soft' : 'outline'}
                >
                  {pluralise(total ?? 0, 'period')}
                </Badge>
                {retired > 0 && (
                  <Badge tone="neutral" variant="outline">
                    {retired} retired
                  </Badge>
                )}
              </div>
            )}
          </div>

          <div className="mt-auto">
            <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
              <span className="sr-only">
                {canWrite ? 'Manage Target periods' : 'View Target periods'}
              </span>
              <span aria-hidden="true">{canWrite ? 'Manage' : 'View'}</span>
            </Button>
          </div>
        </PanelBody>
      </Panel>

      {open && (
        <TargetPeriodListModal
          onClose={() => setOpen(false)}
          state={state}
          rows={rows}
          ability={ability}
          onReload={reload}
          cycles={cycles}
          cyclesLoading={cyclesLoading}
          cyclesFailed={cyclesFailed}
        />
      )}
    </>
  );
}

function TargetPeriodListModal({
  onClose,
  state,
  rows,
  ability,
  onReload,
  cycles,
  cyclesLoading,
  cyclesFailed,
}: {
  onClose: () => void;
  state: ListState;
  rows: TargetPeriodRecord[];
  ability: Ability;
  onReload: () => void;
  cycles: Cycle[];
  cyclesLoading: boolean;
  cyclesFailed: boolean;
}) {
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<TargetPeriodRecord | undefined>(undefined);
  const [deleting, setDeleting] = useState<TargetPeriodRecord | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /*
   * Writes here are `ADMIN_ROLES` on the router however generous the permission
   * set looks — the same profile as the reference masters, so `reference-data`
   * is the slug that answers it. There is no `target-periods` module in the
   * permission catalogue and inventing one would make `enforcedByRole` answer a
   * question nobody has asked the server.
   */
  const canCreate = ability.canCreate('reference-planning');
  const canUpdate = ability.canUpdate('reference-planning');
  const canDelete = ability.canDelete('reference-planning');
  const updateDenial = denialReason(ability, 'update', 'reference-planning');

  const showActions = canUpdate || canDelete;
  const initialLoad = state.loading && rows.length === 0;
  const columns = 6 + (showActions ? 1 : 0);

  /**
   * Retire or restore, as a one-key PATCH.
   *
   * ⚠️ `isActive` alone is safe here even though `kind` is not: the server merges
   * the patch onto the stored row and re-runs both cross-field rules, and a body
   * carrying neither `kind` nor `cycleId` merges to the row's own consistent
   * state. It is the `kind`-only patch that 422s, because that one changes half
   * of a pair.
   */
  async function setActive(row: TargetPeriodRecord, isActive: boolean) {
    if (busy) return;
    setBusy(true);
    setRowError(null);
    try {
      await api.updateTargetPeriod(row.id, { isActive });
      onReload();
    } catch (caught) {
      setRowError(
        caught instanceof ApiError
          ? caught.status === 403
            ? `${denialReason(ability, 'update', 'reference-planning') ?? 'The server refused that.'} Nothing was changed.`
            : caught.message
          : 'Couldn’t reach the server. Nothing was changed.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    if (!deleting || busy) return;
    setBusy(true);
    setDeleteError(null);
    try {
      await api.deleteTargetPeriod(deleting.id);
      setDeleting(null);
      onReload();
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === TARGET_PERIOD_IN_USE) {
        /*
         * ⚠️ Switched on the CODE, never the message — that message has a count
         * in it and the backend reserves the right to reword it. This is the one
         * named refusal in the module; the duplicate-window 409 beside it is
         * deliberately anonymous, so anything else falls through to the server's
         * own sentence below.
         *
         * The message already names how many targets still point here and
         * already says to retire instead, so it is shown as written and the
         * route onward is named rather than restated.
         */
        setDeleteError(
          `${caught.message} Close this and use “Retire” on the row instead — it keeps every target resolving.`,
        );
      } else if (caught instanceof ApiError && caught.status === 404) {
        // Already gone: that is the outcome the user asked for, not an error.
        setDeleting(null);
        onReload();
      } else if (caught instanceof ApiError) {
        setDeleteError(
          caught.status === 403
            ? `${denialReason(ability, 'delete', 'reference-planning') ?? 'The server refused that.'} Nothing was deleted.`
            : caught.message,
        );
      } else {
        setDeleteError('Couldn’t reach the server. Nothing was deleted.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title="Target periods"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            {canCreate && (
              <Button
                variant="primary"
                leadingIcon={<PlusIcon />}
                onClick={() => {
                  setEditing(undefined);
                  setFormOpen(true);
                }}
              >
                New target period
              </Button>
            )}
          </>
        }
      >
        <div className="grid gap-3">
          {/*
            The standing "retire rather than delete" advisory that used to open
            this modal is GONE, and nothing it said was lost: the delete
            confirmation names the same consequence ("Refused while any target
            is still set against it — retire it instead."), and the 409 handler
            surfaces the server's own sentence with the route onward. Stating a
            consequence AT the control that refuses it is where somebody looking
            for it actually is; repeating it as advice over the whole modal is
            helper text.
          */}
          {state.error && (
            <Notice tone="danger" role="alert">
              <span className="[overflow-wrap:anywhere]">
                Couldn’t load target periods. {state.error}
              </span>
            </Notice>
          )}

          {rowError && (
            <Notice tone="danger" role="alert">
              <span className="[overflow-wrap:anywhere]">{rowError}</span>
            </Notice>
          )}

          {/*
            ⚠️ The incomplete-list warning is GONE (2026-09-05). The endpoint's
            ceiling is still asked for and there is still no "load more", so
            page one of several now passes for the whole set with nothing on
            screen saying otherwise.
          */}
          {updateDenial && (
            <p className="m-0 text-xs text-fg-subtle">{updateDenial} You can read it here.</p>
          )}

          {initialLoad ? (
            <TableWrap frozenHeader frozenMaxHeight="min(55vh, 480px)">
              <Table className="min-w-[820px]">
                <PeriodHead showActions={showActions} />
                <TBody>
                  {Array.from({ length: SKELETON_ROWS }, (_, index) => (
                    <TR key={index}>
                      {Array.from({ length: columns }, (_, cell) => (
                        <TD key={cell}>
                          <Skeleton className={cx('h-3', cell === 0 ? 'w-40' : 'w-20')} />
                        </TD>
                      ))}
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          ) : rows.length === 0 ? (
            /*
             * ⚠️ THE DAY-ONE STATE, and it is the one this whole component was
             * built for. `f_target_periods` shipped empty, so this is what a
             * first reader meets — and until now there was no control anywhere in
             * the app that could change it. The copy says what a period is FOR,
             * because somebody arriving here has probably come from a targets
             * dialog that told them a period was missing.
             */
            <EmptyState
              icon={<PeriodRangeIcon />}
              title={state.error ? 'Nothing to show' : 'No target periods yet'}
              description={
                state.error
                  ? 'Couldn’t load the list — see the message above.'
                  : canCreate
                    ? 'Add the first window — usually a quarter or a year.'
                    : `${denialReason(ability, 'create', 'reference-planning') ?? ''} No target can be set until a period exists.`
              }
            />
          ) : (
            <TableWrap frozenHeader frozenMaxHeight="min(55vh, 480px)">
              <Table className="min-w-[820px]">
                <PeriodHead showActions={showActions} />
                <TBody>
                  {rows.map((row) => (
                    <TR key={row.id}>
                      <TH scope="row" className="min-w-[9rem]">
                        <span className="break-words">{row.name}</span>
                      </TH>

                      <TD className="text-xs text-fg-muted">{targetPeriodKindLabel(row.kind)}</TD>

                      {/* Formatted from the plain 'YYYY-MM-DD' string. No Date is
                          constructed on this path — that is how a period
                          boundary loses a day west of Greenwich, which is the
                          difference between two quarters. */}
                      <TD className="text-xs whitespace-nowrap text-fg-muted tabular">
                        {formatDateOnly(row.startDate)}
                      </TD>
                      <TD className="text-xs whitespace-nowrap text-fg-muted tabular">
                        {formatDateOnly(row.endDate)}
                      </TD>

                      {/*
                        ⚠️ `cycleName`, not `name` — d_cycles keeps the catalogue's
                        own column names, and this is the one place the two
                        spellings meet. An em dash here is the NORMAL reading on
                        every kind but `cycle`: the service refuses a stored cycle
                        on any other kind, so null is by construction rather than
                        a value somebody failed to fill in.
                      */}
                      <TD className="text-xs text-fg-muted">
                        <span className="break-words">{orDash(row.cycle?.cycleName ?? null)}</span>
                      </TD>

                      <TD>
                        <CatalogStatusBadge active={row.isActive} />
                      </TD>

                      {showActions && (
                        <TD>
                          <div className="flex items-center justify-end gap-1">
                            {canUpdate && (
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={busy}
                                onClick={() => void setActive(row, !row.isActive)}
                              >
                                {row.isActive ? 'Retire' : 'Restore'}
                              </Button>
                            )}
                            {canUpdate && (
                              <Button
                                variant="ghost"
                                size="sm"
                                iconOnly
                                aria-label={`Edit ${row.name}`}
                                title={`Edit ${row.name}`}
                                disabled={busy}
                                leadingIcon={<PencilIcon />}
                                onClick={() => {
                                  setEditing(row);
                                  setFormOpen(true);
                                }}
                              />
                            )}
                            {canDelete && (
                              <Button
                                variant="ghost"
                                size="sm"
                                iconOnly
                                aria-label={`Delete ${row.name} permanently`}
                                title={`Delete ${row.name} permanently`}
                                disabled={busy}
                                leadingIcon={<TrashIcon />}
                                className="text-fg-subtle hover:text-danger"
                                onClick={() => {
                                  setDeleteError(null);
                                  setDeleting(row);
                                }}
                              />
                            )}
                          </div>
                        </TD>
                      )}
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          )}
        </div>
      </Modal>

      <TargetPeriodFormModal
        open={formOpen}
        period={editing}
        cycles={cycles}
        cyclesLoading={cyclesLoading}
        cyclesFailed={cyclesFailed}
        onClose={() => setFormOpen(false)}
        onSaved={onReload}
      />

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => {
          setDeleting(null);
          setDeleteError(null);
        }}
        onConfirm={() => void confirmDelete()}
        title={`Delete “${deleting?.name ?? ''}” permanently?`}
        confirmLabel="Delete period"
        loading={busy}
        error={deleteError}
        description={
          <>
            <p className="m-0">
              The period <strong>{deleting?.name}</strong> will be deleted. This can't be undone.
            </p>
            <p className="mt-2 mb-0">
              A period with targets set against it can't be deleted. Retire it instead.
            </p>
          </>
        }
      />
    </>
  );
}

function PeriodHead({ showActions }: { showActions: boolean }) {
  return (
    <THead>
      <TR>
        <TH>Period</TH>
        <TH className="w-24">Kind</TH>
        <TH className="w-28">Starts</TH>
        <TH className="w-28">Ends</TH>
        <TH className="w-40">Cycle</TH>
        <TH className="w-28">Status</TH>
        {showActions && (
          <TH className="w-56">
            <span className="sr-only">Actions</span>
          </TH>
        )}
      </TR>
    </THead>
  );
}
